import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import { AREA_COLORS, OTHER_COLOR, StackedBars } from '@/components/charts';
import {
  Bullet,
  Card,
  CustomersToggle,
  Empty,
  HBars,
  Kpi,
  Loaded,
  PageHead,
  PillTag,
  RangeSeg,
  Select,
} from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { countDelta, fmtDay, fmtMoney, fmtPct } from '@/lib/format';
import { RANGES, useCustomers, useParam } from '@/lib/params';
import type { Cost } from '@/lib/types';

function Body({ d }: { d: Cost }) {
  const navigate = useNavigate();
  const k = d.kpis;
  const series = d.stack.areas.map((a, i) => ({
    key: a.key,
    label: a.label,
    color: a.key === '_other' ? OTHER_COLOR : AREA_COLORS[i],
    values: d.stack.series[a.key] ?? [],
  }));
  return (
    <>
      <div className="kpis">
        <Kpi label="AI spend" value={fmtMoney(k.spend.value)} delta={countDelta(k.spend.value, k.spend.prev, false)} />
        <Kpi
          label={`${k.projected.month}, projected`}
          value={fmtMoney(k.projected.value)}
          footer={<span className="d muted">at the current daily rate</span>}
        />
        <Kpi
          label="Per successful ask"
          value={fmtMoney(k.per_ok_ask.value)}
          delta={countDelta(k.per_ok_ask.value, k.per_ok_ask.prev, false)}
        />
        <Kpi
          label="Spent on not-ok asks"
          value={fmtMoney(k.not_ok_spend.value)}
          footer={<span className="d muted">{fmtPct(k.not_ok_spend.share)} of spend</span>}
        />
        <Kpi
          label="Read from prompt cache"
          value={fmtPct(k.cache_share)}
          footer={<span className="d muted">of input tokens</span>}
        />
      </div>

      <Card
        style={{ marginTop: 12 }}
        title="Spend per day by area"
        right={
          <span className="legend">
            {series.map((s) => (
              <span key={s.key}>
                <i style={{ background: s.color, width: 8, height: 8 }} />
                {s.label}
              </span>
            ))}
          </span>
        }
      >
        {series.length ? (
          <StackedBars
            labels={d.stack.days.map(fmtDay)}
            series={series}
            format={fmtMoney}
            title={(i) => fmtDay(d.stack.days[i])}
          />
        ) : (
          <Empty icon="◌" title="No AI spend in this range" />
        )}
      </Card>

      <div className="g2">
        <Card
          title="Companies closest to their AI budget"
          right={
            <span className="faint" style={{ fontSize: 12 }}>
              tick = how far into the month
            </span>
          }
        >
          {d.budgets.length === 0 ? (
            <div className="muted">No company has an AI budget.</div>
          ) : (
            <table>
              <tbody>
                {d.budgets.map((b) => (
                  <tr key={b.company_id} onClick={() => navigate(`/accounts/${b.company_id}`)}>
                    <td>
                      <b>{b.name}</b>
                      <div className="faint" style={{ fontSize: 11 }}>
                        {[b.plan_key, `${fmtMoney(b.budget)} a month`].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td>
                      <Bullet share={b.share} pace={b.pace} width={180} />
                    </td>
                    <td className="num">{fmtPct(b.share)}</td>
                    <td>{b.status && <PillTag pill={b.status} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card title="Most expensive asks">
          {d.expensive.length === 0 ? (
            <div className="muted">No asks in this range.</div>
          ) : (
            d.expensive.map((e) => (
              <div
                key={e.ask_id}
                className="sample clickable"
                onClick={() => navigate(`/feed?sel=ask::${e.ask_id}&customers=false`)}
              >
                <b style={{ width: 56, flexShrink: 0 }}>{fmtMoney(e.cost)}</b>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="clamp2">
                    {e.text ? `“${e.text}”` : <span className="faint">(text cleared after 90 days)</span>}
                  </div>
                  <div className="faint" style={{ fontSize: 12 }}>
                    {[e.company?.name ?? 'No company', e.area_label, `${e.calls} calls`].join(' · ')}
                  </div>
                </div>
                <span className={`pill ${e.status === 'ok' ? 'good' : 'bad'}`}>
                  {e.status === 'ok' ? 'ok' : 'failed'}
                </span>
              </div>
            ))
          )}
        </Card>
      </div>

      <div className="g3">
        <Card title="By model">
          <HBars rows={d.by_model.map((m) => ({ key: m.label, label: m.label, value: m.cost }))} format={fmtMoney} />
        </Card>
        <Card title="Cost per ask by area">
          <HBars
            rows={d.per_ask_by_area.map((a) => ({ key: a.key, label: a.label, value: a.cost_per_ask }))}
            format={fmtMoney}
          />
        </Card>
        <Card title="Not tied to a user ask">
          {d.untied.length === 0 ? (
            <div className="muted">All spend in this range came from asks.</div>
          ) : (
            <div className="kv" style={{ gridTemplateColumns: '1fr auto' }}>
              {d.untied.map((u) => (
                <Fragment key={u.key}>
                  <span className="k">{u.label}</span>
                  <b>{fmtMoney(u.cost)}</b>
                </Fragment>
              ))}
            </div>
          )}
          <div className="note">Background AI work (skill sweeps, workflow runs, processing), by area.</div>
        </Card>
      </div>
    </>
  );
}

export function CostPage() {
  const meta = useMeta();
  const [range] = useParam('range', '30d');
  const [area, setArea] = useParam('area', '');
  const [customers] = useCustomers();
  const query = useApi<Cost>('/cost', { range, area, customers });
  const w = query.data?.window;
  return (
    <div className="page">
      <PageHead
        title="Cost"
        subtitle={w ? `AI spend from llm_request_logs · ${fmtDay(w.start)} – ${fmtDay(w.end)}` : ' '}
      >
        <RangeSeg options={RANGES.cost} fallback="30d" />
        <Select
          value={area}
          active={Boolean(area)}
          onChange={(v) => setArea(v || null)}
          options={[
            { value: '', label: 'All areas' },
            ...(meta.data?.areas ?? []).map((a) => ({ value: a.key, label: a.label })),
          ]}
        />
        <CustomersToggle />
      </PageHead>
      <Loaded query={query}>{(d) => <Body d={d} />}</Loaded>
    </div>
  );
}
